/* eslint-disable no-nested-ternary, css-modules/no-unused-class  */
import { FC, memo, useState, useRef, useEffect } from 'react';
import { Divider, Icon, Button } from 'common-components';
import classNames from 'classnames';
import { ContentCardProps } from './contentCardTypes';
import { useTranslationContext } from '../contexts/translationContext';
import styles from './contentCard.module.scss';
import Tooltip from '../tooltip/tooltip';
import LeanHover from './leanHover';
import ChipsWrapper from '../adminPanel/chipsWrapper/chipsWrapper';
import Links from './links';
import getIconByTags from './languageToIcon';

const ContentCard: FC<ContentCardProps> = (props: ContentCardProps) => {
    const {
        onClick,
        type,
        name,
        displayName,
        score,
        imageUrl,
        tags,
        timeOrDate,
        menuActions,
        cardUrl,
        count,
        onCompletionCertificateClick,
        isLeanDisabled,
        earnedPoints,
        doneRecently,
        month,
        onContactUsClick,
        status,
        description,
        dueDate,
        disabled,
        hasCertificate,
        pointer = true,
        isCourseCreationPreview,
        links,
        tagsNames,
        systemCourseDisplayName,
        customCourseDisplayNames,
        tagline
    } = props;
    const [menuOpen, setMenuOpen] = useState<boolean>(false);
    const [isHovered, setIsHovered] = useState<boolean>(false);
    const [isInfoHovered, setIsInfoHovered] = useState<boolean>(false);
    const ref = useRef() as React.MutableRefObject<HTMLInputElement>;
    const { translate } = useTranslationContext();
    const isVideoTutorial = type === 'videoTutorial';
    const languageIcon = getIconByTags(tagsNames || []);

    useEffect(() => {
        const checkIfClickedOutside = (e: any) => {
            if (menuOpen && ref.current && !ref.current.contains(e.target)) {
                setMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', checkIfClickedOutside);
        return () => {
            document.removeEventListener('mousedown', checkIfClickedOutside);
        };
    }, [menuOpen]);

    const typeProperties: {
        [type: string]: {
            color: string;
            iconName: string;
            text: string;
            borderColor?: string;
            hoveredBorderColor?: string;
            backgroundColor?: string;
        };
    } = {
        lesson: {
            color: '#FB86B6',
            iconName: 'lessonIconLearningPanel',
            text: translate('LESSON'),
            backgroundColor: 'rgba(251, 134, 182, 0.15)'
        },
        course: {
            color: '#86D1FB',
            iconName: 'courseIconLearningPanel',
            text: translate('COURSE'),
            backgroundColor: 'rgba(134, 209, 251, 0.15)'
        },
        challenge: {
            color: '#86FBBC',
            iconName: 'challengeIconLearningPanel',
            text: translate('CHALLENGE'),
            backgroundColor: 'rgba(134, 251, 188, 0.15)'
        },
        assessment: {
            color: '#FF5E7B',
            iconName: 'assessmentIconLearningPanel',
            text: translate('ASSESSMENT'),
            backgroundColor: 'rgba(255, 94, 123, 0.2)'
        },
        tournament: {
            color: '#F7F9A8',
            iconName: 'tournamentIconLearningPanel',
            text: translate('TOURNAMENT'),
            backgroundColor: 'rgba(247, 249, 168, 0.15)'
        },
        videoTutorial: {
            color: 'rgba(207, 134, 251, 1)',
            iconName: 'videoLearningPanel',
            text: translate('VIDEO'),
            backgroundColor: 'rgba(207, 134, 251, 0.15)'
        },
        skillTree: {
            color: '#f5d070',
            iconName: 'skillTree',
            text: translate('SkillTree'),
            backgroundColor:
                'linear-gradient(277deg, rgba(255, 252, 225, 0.15) -126.9%, rgba(255, 242, 194, 0.15) -104.52%, rgba(255, 233, 163, 0.15) -77.13%, rgba(255, 220, 122, 0.15) -63.59%, rgba(255, 221, 127, 0.15) -25.03%, rgba(208, 160, 72, 0.15) -9.68%, rgba(208, 160, 72, 0.15) 5.07%, rgba(255, 220, 122, 0.15) 48.46%, rgba(208, 160, 72, 0.15) 96.22%, rgba(193, 140, 55, 0.15) 111.25%, rgba(167, 115, 30, 0.15) 134.3%, rgba(136, 84, 0, 0.15) 207.11%)'
        },
        learningPath: {
            color: '#f5d070',
            iconName: 'path',
            text: translate('LearningPath'),
            backgroundColor: 'rgba(0, 0, 0, 0.69)'
        },
        lab: {
            color: '#DD494F',
            backgroundColor: 'rgba(251, 134, 182, 0.15)',
            iconName: 'vulnerabilityFix',
            text: translate('VulnerabilityFix')
        }
    };

    const getDueDate = () => {
        const borderColors = {
            skillTree: 'rgba(245, 208, 112, 0.5)',
            learningPath: 'rgba(251, 134, 182, 0.35)',
            course: 'rgba(132, 207, 248, 0.35)',
            lesson: 'rgba(246, 133, 182, 0.35)'
        };

        const today = new Date();
        const diffTime = (dueDate?.getTime() || 0) - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

        const dueDateText =
            diffDays > 0
                ? `${translate(diffDays === 1 ? 'XDayLeft' : 'XDaysLeft', { days: diffDays })}`
                : translate(diffDays === -1 ? 'XDayOverdue' : 'XDaysOverdue', { days: Math.abs(diffDays) });
        const dueDateClass =
            diffDays > 3
                ? styles['due-date']
                : diffDays > 0
                ? styles['about-to-expire-due-date']
                : styles['overdue-date'];

        return (
            <div className={dueDateClass} style={{ '--borderColor': borderColors[type] } as any}>
                <Icon name={diffDays > 0 ? 'clock' : 'warningTriangle'} />
                <div>{dueDateText}</div>
            </div>
        );
    };

    const getDuration = (timeOrDate: string | undefined): string | undefined => {
        if (!timeOrDate) return undefined;
        const timeNumber = Number(timeOrDate);

        if (timeOrDate.search(/[a-zA-Z]/) !== -1) return timeOrDate;
        if (timeNumber < 60 || timeOrDate.indexOf('-') !== -1) return timeOrDate + translate('min');

        let result = `${Math.floor(timeNumber / 60)}${translate('h')}`;
        if (Math.floor(timeNumber % 60) !== 0) {
            result += `:${Math.floor(timeNumber % 60)}${translate('min')}`;
        }
        return result;
    };

    const IconRenderer = ({ type }) => {
        if (typeProperties[type] === undefined) {
            return <Icon name="default-icon" className={styles['type-icon']} />;
        }

        return <Icon name={typeProperties[type]?.iconName} className={styles['type-icon']} />;
    };

    const renderVideoTutorialTag = () => {
        return (
            (month || status !== 'notStarted') && (
                <div className={classNames(styles['completed-tag'], styles['video-tutorial'])}>
                    <div className={styles['earn-wrapper']}>
                        <div className={styles.text}>{month || translate(status)}</div>
                        {status !== 'notStarted' && !(!month && status === 'inProgress') && (
                            <>
                                <Divider
                                    orientation="vertical"
                                    className={styles.divider}
                                    color="rgba(255, 255, 255, 0.5)"
                                />
                                {status === 'completed' ? (
                                    <>
                                        {month && <Icon name="done" className={styles['max-score-icon']} />}
                                        <Icon name="devCoin" className={styles['max-score-icon']} />
                                        {earnedPoints && (
                                            <div className={styles.score}>{earnedPoints.toLocaleString()}</div>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <Icon name="resume" className={styles['progress-icon']} />
                                        <div className={styles.resume}>{translate('Resume')}</div>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )
        );
    };

    const dateTime = getDuration(timeOrDate);

    return (
        <div
            style={{ position: 'relative' }}
            onMouseEnter={(): void => setIsHovered(true)}
            onMouseLeave={(): void => setIsHovered(false)}
        >
            {isHovered && status === 'completed' && type === 'course' && !isInfoHovered && (
                <div className={styles['hovered-wrapper']}>
                    <div className={styles['hovered-card']}></div>
                    <div className={styles['hovered-card-items']}>
                        <div
                            className={styles['info-detail']}
                            onClick={e => {
                                setIsInfoHovered(true);
                                e.stopPropagation();
                                e.preventDefault();
                            }}
                        >
                            <Icon name="info" />
                        </div>
                        <div className={styles.button}>
                            {hasCertificate ? (
                                <Button
                                    lowerCase
                                    type="gradient"
                                    text={translate('CompletionCertificate')}
                                    onClick={() => onCompletionCertificateClick?.()}
                                />
                            ) : (
                                <a href={cardUrl}>
                                    <Button lowerCase type="gradient" text={translate('RetakeCourse')} />
                                </a>
                            )}
                        </div>
                        {hasCertificate && (
                            <a href={cardUrl}>
                                <div className={styles.retake}>{translate('RetakeCourse')}</div>
                            </a>
                        )}
                    </div>
                </div>
            )}
            {isInfoHovered && description && (
                <div className={styles['hovered-wrapper']}>
                    <div className={styles['hovered-card']}></div>
                    <div className={styles['hovered-card-description']}>
                        <div className={styles.back} onClick={() => setIsInfoHovered(false)}>
                            <Icon name="back" />
                            <div>{translate('Back')}</div>
                        </div>
                        <div className={styles.name}>{displayName}</div>
                        <div className={styles.description}>{description}</div>
                        <div className={styles.links}>
                            {links && (
                                <>
                                    <div className={styles.tags}>
                                        {links.length > 0 && <Icon name="openInNewTab" className={styles.icon} />}
                                        {links.length > 0 && <Links links={links} width="17rem" />}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {isLeanDisabled && (
                <div className={styles['lean-disable-layer-wrapper']}>
                    <div className={styles['lean-disable-layer']}>
                        <img
                            src={`${process.env.PUBLIC_URL}/images/leanContentLayer.svg`}
                            className={styles.image}
                            alt="contentCardLayer"
                        />
                    </div>
                </div>
            )}
            {isHovered && isLeanDisabled && (
                <LeanHover
                    type={type}
                    cardUrl={cardUrl}
                    tags={links}
                    onContactUsClick={onContactUsClick}
                    description={description}
                />
            )}
            <a href={cardUrl}>
                <div
                    onClick={() => (!isLeanDisabled ? onClick?.() : undefined)}
                    id={`${type}-content-card-${name}`}
                    className={classNames(
                        isCourseCreationPreview ? styles['not-hovered-content-card'] : styles['content-card'],
                        {
                            [styles['disabled-content-card']]: disabled,
                            [styles['course-border']]: type === 'course' && !isCourseCreationPreview,
                            [styles['skill-tree-border']]: type === 'skillTree',
                            [styles['lesson-border']]: type === 'lesson',
                            [styles['video-border']]: type === 'videoTutorial',
                            [styles['learning-path-border']]: type === 'learningPath',
                            [styles['vulnerability-fix-border']]: type === 'lab'
                        }
                    )}
                    style={{ cursor: pointer ? 'pointer' : 'default' }}
                >
                    {isVideoTutorial && renderVideoTutorialTag()}
                    <div className={styles.top}>
                        <img src={imageUrl} className={styles.img} alt="imageOverlay" loading="lazy" />
                        {score !== 0 && score && (
                            <div className={styles.score}>
                                <Icon name="devCoin" className={styles.icon} />
                                <div className={styles['score-text']}>{score.toLocaleString()}</div>
                            </div>
                        )}
                        {languageIcon && (
                            <img
                                src={`${process.env.PUBLIC_URL}/images/${languageIcon}.webp`}
                                className={styles['language-icon']}
                                alt="languageIcon"
                            />
                        )}
                        {dueDate && getDueDate()}
                        {menuActions && (
                            <div ref={ref} className={styles.menu} onClick={() => setMenuOpen(!menuOpen)}>
                                <Icon name="menu" className={styles['menu-icon']} />
                            </div>
                        )}
                        {(timeOrDate || isCourseCreationPreview) && (
                            <div className={styles.detail}>
                                {isCourseCreationPreview ? (
                                    <>
                                        <Tooltip type="secondary" title={translate('isCourseCreationPreview')}>
                                            <span>
                                                <Icon name="adminWarning" />
                                            </span>
                                        </Tooltip>
                                        <div style={{ fontStyle: 'italic', marginLeft: '0.3rem' }}>
                                            {translate('Duration')}
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div>{dateTime}</div>
                                        {description && <hr />}
                                        {description && (
                                            <div
                                                className={styles['info-icon-wrapper']}
                                                onClick={e => {
                                                    setIsInfoHovered(true);
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                }}
                                            >
                                                <Icon name="info" style={{ marginRight: '0.2rem' }} />
                                                <div>{translate('info')}</div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                        <div
                            className={styles.type}
                            style={
                                {
                                    '--color': typeProperties[type]?.color,
                                    '--backgroundColor': typeProperties[type]?.backgroundColor
                                } as any
                            }
                        >
                            {IconRenderer({ type })}
                            {count && (
                                <div
                                    className={styles.text}
                                    style={
                                        {
                                            '--color': typeProperties[type]?.color,
                                            '--backgroundColor': typeProperties[type]?.backgroundColor
                                        } as any
                                    }
                                >
                                    {typeProperties[type]?.text} ({count} lessons)
                                </div>
                            )}
                            {!count && (
                                <div
                                    className={type === 'learningPath' ? styles['learning-path-text'] : styles.text}
                                    style={
                                        {
                                            '--color': typeProperties[type]?.color,
                                            '--backgroundColor': typeProperties[type]?.backgroundColor
                                        } as any
                                    }
                                >
                                    {typeProperties[type]?.text}
                                </div>
                            )}
                        </div>
                        {menuOpen && (
                            <div className={styles['menu-wrapper']}>
                                {menuActions?.map(action => (
                                    <div
                                        key={action.text}
                                        className={styles['menu-item']}
                                        onClick={() => action.onClick()}
                                    >
                                        <Icon className={styles['menu-item-icon']} name={action.iconName} />
                                        <div>{action.text}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className={styles.bottom}>
                        <div className={styles.name}>{displayName}</div>
                        <hr />
                        <div className={styles['tags-wrapper']}>
                            {type === 'lesson' && (
                                <div className={styles['tags-title']}>
                                    {systemCourseDisplayName || customCourseDisplayNames ? (
                                        <>
                                            <Icon name="courseIconLearningPanel" />
                                            <span className={styles['course-name-truncate']}>
                                                {translate('In')} {systemCourseDisplayName || customCourseDisplayNames}
                                            </span>
                                        </>
                                    ) : (
                                        <div className={styles['course-name-truncate']}>{description}</div>
                                    )}
                                </div>
                            )}
                            {type === 'course' && <div className={styles['tags-tagline']}>{tagline}</div>}
                            {(type === 'lab' || type === 'skillTree' || type === 'learningPath') && (
                                <div className={styles['tags-tagline']}>{description}</div>
                            )}

                            {tags && tags.length > 0 && (
                                <div className={styles['tags-position']}>
                                    <ChipsWrapper
                                        itemsText={tags || []}
                                        width={'100%'}
                                        fontSize="13px"
                                        fontColor="rgba(225, 225, 225, 0.70)"
                                        padding="2.5px 9px"
                                        itemStyle={{
                                            borderRadius: '2rem',
                                            border: '1px solid rgba(255, 255, 255, 0.30)',
                                            background: 'rgba(255, 255, 255, 0.01)',
                                            boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.50)',
                                            backdropFilter: 'blur(11px)'
                                        }}
                                        moreColor="rgba(225, 225, 225, 0.70)"
                                        withBorder
                                        tooltipType="primary"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </a>
            {(status === 'completed' || status === 'inProgress') && !isVideoTutorial && (
                <div
                    className={classNames(styles['completed-tag'], {
                        [styles.course]: type === 'course',
                        [styles['skill-tree']]: type === 'skillTree',
                        [styles.lesson]: type === 'lesson',
                        [styles['learning-path']]: type === 'learningPath',
                        [styles['vulnerability-fix']]: type === 'lab'
                    })}
                >
                    {earnedPoints ? (
                        <div className={styles['earn-wrapper']}>
                            <div className={styles.text}>{translate('earned')}</div>
                            <Divider
                                orientation="vertical"
                                className={styles.divider}
                                color="rgba(255, 255, 255, 0.5)"
                            />
                            <Icon name="devCoin" className={styles['max-score-icon']} />
                            {earnedPoints && (
                                <div className={styles.score}>{Number(earnedPoints).toLocaleString()}</div>
                            )}
                            {doneRecently && (
                                <Tooltip
                                    key="earn-more-points"
                                    placement="bottom"
                                    title={<div>{translate('NoCoins6Months')}</div>}
                                    maxWidth="11rem"
                                >
                                    <div>
                                        <Icon name="info" className={styles['info-icon']} />
                                    </div>
                                </Tooltip>
                            )}
                        </div>
                    ) : (
                        translate(status)
                    )}
                </div>
            )}
        </div>
    );
};

export default memo(ContentCard);
